import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import UsageWidget from '../components/dashboard/UsageWidget';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, Minus, Flame, Zap,
  AlertTriangle, CheckCircle2, Circle, Clock, Sparkles,
  ChevronRight, Building, Loader2, Mail, Video, FileText,
  Swords, Phone, CalendarPlus, X, Target, ListTodo,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const URGENCY = {
  Urgent: 'bg-red-500/10 text-red-600 border-red-500/20',
  'AI tip': 'bg-primary/10 text-primary border-primary/20',
  Opportunity: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
};

const TASK_TYPES = {
  call: { label: 'Call', color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  email: { label: 'Email', color: 'bg-violet-500/10 text-violet-600 border-violet-500/20' },
  meeting: { label: 'Meeting', color: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
  document: { label: 'Document', color: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' },
};

function inferTaskType(title) {
  const t = title.toLowerCase();
  if (t.includes('email') || t.includes('send') || t.includes('recap')) return 'email';
  if (t.includes('demo') || t.includes('meeting') || t.includes('schedule')) return 'meeting';
  if (t.includes('document') || t.includes('proposal') || t.includes('prepare')) return 'document';
  return 'call';
}

function addToGoogleCalendar(item) {
  const title = encodeURIComponent(item.title);
  const details = encodeURIComponent(`${item.context}\n\nCompany: ${item.company || '-'}`);
  const due = item.due_date ? new Date(item.due_date) : new Date(Date.now() + 3 * 86400000);
  const start = due.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  const end = new Date(due.getTime() + 3600000).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  window.open(`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&details=${details}&dates=${start}/${end}`, '_blank');
}

// ─── Insights Tooltip ───
function InsightsTooltip({ insights, onClose }) {
  if (!insights) return null;
  return (
    <div className="absolute right-0 top-full mt-1 z-50 w-72 p-3 rounded-lg bg-white border border-border shadow-card-hover animate-fade-in" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-foreground">{insights.company} — {insights.date}</span>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></button>
      </div>
      <div className="space-y-1.5 text-xs">
        {insights.contact_role && <p className="text-muted-foreground">Contact: <span className="text-foreground">{insights.contact_role}</span></p>}
        {insights.sentiment && <p className="text-muted-foreground">Sentiment: <span className={insights.sentiment === 'Positive' ? 'text-emerald-600' : insights.sentiment === 'Negative' ? 'text-red-600' : 'text-foreground'}>{insights.sentiment}</span></p>}
        {insights.risk && <p className="text-muted-foreground">Risk: <span className={insights.risk === 'High' ? 'text-red-600' : insights.risk === 'Medium' ? 'text-amber-600' : 'text-emerald-600'}>{insights.risk}</span></p>}
        {insights.strength && <p className="text-emerald-600 flex items-start gap-1"><CheckCircle2 className="w-3 h-3 shrink-0 mt-0.5" />{insights.strength}</p>}
        {insights.watch_out && <p className="text-amber-600 flex items-start gap-1"><AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />{insights.watch_out}</p>}
        {insights.next_step && <p className="text-primary flex items-start gap-1"><Zap className="w-3 h-3 shrink-0 mt-0.5" />{insights.next_step}</p>}
      </div>
    </div>
  );
}

export default function RepDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [taskSummary, setTaskSummary] = useState(null);
  const [dashDeals, setDashDeals] = useState([]);
  const [recentCalls, setRecentCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openTooltip, setOpenTooltip] = useState(null);

  // Persisted to URL (?period=weekly|monthly|all). Default = monthly.
  const validPeriods = ['weekly', 'monthly', 'all'];
  const initialPeriod = validPeriods.includes(searchParams.get('period'))
    ? searchParams.get('period')
    : 'monthly';
  const [timePeriod, setTimePeriodState] = useState(initialPeriod);
  const setTimePeriod = (val) => {
    setTimePeriodState(val);
    const next = new URLSearchParams(searchParams);
    next.set('period', val);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [timePeriod]);

  const fetchAll = async () => {
    try {
      const [repRes, taskRes, sumRes, dealsRes, callsRes] = await Promise.all([
        fetch(`${API_URL}/api/dashboard/rep?period=${timePeriod}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/tasks?status=all`, { credentials: 'include' }),
        fetch(`${API_URL}/api/tasks/summary`, { credentials: 'include' }),
        fetch(`${API_URL}/api/dashboard/deals?period=${timePeriod}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/calls?archived=false`, { credentials: 'include' }),
      ]);
      if (repRes.ok) setData(await repRes.json());
      if (taskRes.ok) setTasks(await taskRes.json());
      if (sumRes.ok) setTaskSummary(await sumRes.json());
      if (dealsRes.ok) {
        const d = await dealsRes.json();
        setDashDeals(d.sort((a, b) => b.score - a.score).slice(0, 4));
      }
      if (callsRes.ok) {
        const c = await callsRes.json();
        setRecentCalls(c.filter(x => x.status === 'completed').slice(0, 4));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const toggleTask = async (task) => {
    const next = task.status === 'done' ? 'todo' : 'done';
    try {
      await fetch(`${API_URL}/api/tasks/${task.task_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ status: next }),
      });
      fetchAll();
    } catch (e) { console.error(e); }
  };

  const formatValue = (v) => {
    if (!v) return '';
    if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
    if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
    return `$${v}`;
  };

  const getWP = (s) => {
    if (s >= 90) return { pct: 95, color: 'text-emerald-600', bg: 'bg-emerald-500', label: 'Very High' };
    if (s >= 75) return { pct: Math.round(70 + (s - 75) / 15 * 20), color: 'text-emerald-600', bg: 'bg-emerald-500', label: 'High' };
    if (s >= 60) return { pct: Math.round(50 + (s - 60) / 15 * 20), color: 'text-amber-600', bg: 'bg-amber-500', label: 'Promising' };
    if (s >= 40) return { pct: Math.round(30 + (s - 40) / 20 * 20), color: 'text-amber-600', bg: 'bg-amber-500', label: 'Medium' };
    return { pct: Math.round(10 + s / 40 * 20), color: 'text-red-600', bg: 'bg-red-500', label: 'Low' };
  };
  const classBadge = (g) => ({ 'Hot Deal': 'bg-orange-500/15 text-orange-600 border-orange-500/30', 'SQL': 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30', 'Warm Lead': 'bg-yellow-500/15 text-amber-600 border-yellow-500/30', 'Cold Lead': 'bg-blue-500/15 text-blue-600 border-blue-500/30', 'Disqualified': 'bg-red-500/15 text-red-600 border-red-500/30' }[g] || 'bg-secondary text-muted-foreground border-border');
  const riskBadge = (r) => (r === 'High' || r === 'Critical') ? 'bg-red-500/15 text-red-600 border-red-500/30' : r === 'Medium' ? 'bg-yellow-500/15 text-amber-600 border-yellow-500/30' : 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  if (loading) return <Layout><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div></Layout>;
  if (!data) return <Layout><p className="text-center py-16 text-muted-foreground">Failed to load dashboard</p></Layout>;

  const { kpi, streak, skills, coaching_priority, insights, focus } = data;
  const now = new Date();
  const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const overdueTasks = tasks.filter(t => t.status !== 'done' && t.due_date && new Date(t.due_date) < now);
  const todayTasks = tasks.filter(t => t.status !== 'done' && t.due_date && new Date(t.due_date) >= now && new Date(t.due_date) < todayEnd);
  const doneToday = tasks.filter(t => t.status === 'done' && t.updated_at && new Date(t.updated_at).toDateString() === now.toDateString());
  const totalToday = overdueTasks.length + todayTasks.length + doneToday.length;
  const pendingCount = (taskSummary?.todo || 0) + (taskSummary?.in_progress || 0);

  return (
    <Layout>
      <div className="space-y-5" data-testid="rep-dashboard">
        {/* ─── Header with period selector ─── */}
        <div className="flex items-center justify-end">
          <Select value={timePeriod} onValueChange={setTimePeriod}>
            <SelectTrigger className="w-28 h-9 text-xs bg-card border-border" data-testid="time-period-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* ─── Streak Banner ─── */}
        <div className="flex items-center gap-4 px-5 py-3 rounded-xl bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/15" data-testid="streak-banner">
          <div className="flex items-center gap-2">
            <Flame className="w-6 h-6 text-primary" />
            <span className="font-heading text-xl font-bold text-primary">{streak.days}</span>
            <span className="text-sm text-muted-foreground">day streak</span>
          </div>
          <div className="h-5 w-px bg-border" />
          <p className="text-sm text-foreground/80">{streak.motivation}</p>
        </div>

        {/* ─── KPI Strip (5 cards: Win Rate, Open Deals, Coaching Score, Calls, Tasks) ─── */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="kpi-strip">
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Win Rate</p>
              <div className="flex items-end gap-1.5">
                <span className="font-heading text-2xl font-bold text-foreground">{kpi.win_rate}%</span>
                {kpi.win_rate_delta !== 0 && <span className={`text-xs font-semibold mb-0.5 ${kpi.win_rate_delta > 0 ? 'text-emerald-600' : 'text-red-600'}`}>{kpi.win_rate_delta > 0 ? '+' : ''}{kpi.win_rate_delta}%</span>}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Open Deals</p>
              <span className="font-heading text-2xl font-bold text-foreground">{kpi.open_deals}</span>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Coaching Score</p>
              <div className="flex items-end gap-1.5">
                <span className="font-heading text-2xl font-bold text-foreground">{kpi.coaching_score}</span>
                {kpi.coaching_delta !== 0 && <span className={`text-xs font-semibold mb-0.5 ${kpi.coaching_delta > 0 ? 'text-emerald-600' : 'text-red-600'}`}>{kpi.coaching_delta > 0 ? '+' : ''}{kpi.coaching_delta}</span>}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Calls This Week</p>
              <span className="font-heading text-2xl font-bold text-foreground">{kpi.calls_this_week}</span>
            </CardContent>
          </Card>
          <Card className="bg-primary/10 border-primary/20 cursor-pointer hover:border-primary/40 hover:bg-primary/15 transition-colors" onClick={() => navigate('/tasks')} data-testid="rep-dashboard-tasks-kpi-card">
            <CardContent className="p-4">
              <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Tasks</p>
              <div className="flex items-end gap-1.5">
                <span className={`font-heading text-2xl font-bold ${(taskSummary?.overdue || 0) > 0 ? 'text-red-600' : 'text-foreground'}`}>{pendingCount}</span>
                {(taskSummary?.overdue || 0) > 0 && <span className="text-[10px] font-semibold text-red-600 mb-0.5">{taskSummary.overdue} overdue</span>}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Plan usage meters */}
        <UsageWidget />


        {/* ─── Today's Focus (2/3) + Skills Progress (1/3) — same height ─── */}
        <div className="grid gap-5 lg:grid-cols-3">
          {/* Today's Focus - 2/3 */}
          <div className="lg:col-span-2">
            <Card className="bg-card border-border/50 h-full flex flex-col" data-testid="todays-focus">              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-foreground flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4 text-primary" />Today's Focus
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden">
                {focus.length > 0 ? (
                  <div className="max-h-[260px] overflow-y-auto space-y-2 pr-1">
                    {focus.map((item, i) => (
                      <div key={`k1-${i}`} className="relative flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border/50 hover:border-primary/20 transition-colors group" data-testid={`focus-item-${i}`}>
                        <Badge className={`text-[10px] px-2 py-0.5 border shrink-0 ${URGENCY[item.urgency] || URGENCY['AI tip']}`}>{item.urgency}</Badge>
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => item.call_id ? navigate(`/report/${item.call_id}`) : null}>
                          <p className="text-sm text-foreground truncate">{item.title}</p>
                          <p className="text-[11px] text-muted-foreground truncate">{item.context}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {item.insights && (
                            <button className="p-1 rounded text-muted-foreground/50 hover:text-primary hover:bg-primary/5 transition-colors" onClick={(e) => { e.stopPropagation(); setOpenTooltip(openTooltip === i ? null : i); }} title="View call insights" data-testid={`focus-insights-${i}`}>
                              <Zap className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button className="p-1 rounded text-muted-foreground/50 hover:text-primary hover:bg-primary/5 transition-colors" onClick={(e) => { e.stopPropagation(); addToGoogleCalendar(item); }} title="Add to Google Calendar" data-testid={`focus-calendar-${i}`}>
                            <CalendarPlus className="w-3.5 h-3.5" />
                          </button>
                          <ChevronRight className="w-4 h-4 text-muted-foreground/30" />
                        </div>
                        {openTooltip === i && <InsightsTooltip insights={item.insights} onClose={() => setOpenTooltip(null)} />}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">No focus items for today</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Skills Progress - 1/3 */}
          <div>
            <Card className="bg-card border-border/50 h-full flex flex-col" data-testid="skills-panel">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-foreground">Skills Progress</CardTitle></CardHeader>
              <CardContent className="flex-1 space-y-3">
                {skills.map(s => {
                  const isPri = coaching_priority === s.label;
                  const icon = s.trend === 'up' ? <TrendingUp className="w-3.5 h-3.5 text-emerald-600" /> : s.trend === 'down' ? <TrendingDown className="w-3.5 h-3.5 text-red-600" /> : <Minus className="w-3.5 h-3.5 text-muted-foreground" />;
                  return (
                    <div key={s.key} className={`p-2.5 rounded-lg ${isPri ? 'bg-amber-500/[0.06] border border-amber-500/20' : ''}`} data-testid={`skill-${s.key}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-medium text-foreground">{s.label}</span>
                          {icon}
                          {isPri && <Badge className="text-[9px] px-1.5 py-0 bg-amber-500/15 text-amber-600 border-amber-500/20 border">Priority</Badge>}
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-bold text-foreground">{s.score}</span>
                          {s.delta !== 0 && <span className={`text-[10px] font-semibold ${s.delta > 0 ? 'text-emerald-600' : 'text-red-600'}`}>{s.delta > 0 ? '+' : ''}{s.delta}</span>}
                        </div>
                      </div>
                      <Progress value={s.score} className="h-1.5" />
                    </div>
                  );
                })}
                {coaching_priority && (
                  <Button variant="outline" className="w-full text-xs h-8 mt-1 border-primary/30 text-primary hover:bg-primary/5" onClick={() => navigate('/practice')} data-testid="coaching-btn">
                    <Swords className="w-3.5 h-3.5 mr-1.5" />Get coaching on {coaching_priority}
                  </Button>
                )}

                {/* AI Coaching Insights inline */}
                {insights.length > 0 && (
                  <div className="pt-2 mt-2 border-t border-border/30 space-y-2">
                    <p className="text-[11px] font-semibold text-muted-foreground flex items-center gap-1"><Sparkles className="w-3 h-3 text-primary" />AI Insights</p>
                    {insights.map((ins, i) => (
                      <div key={`k2-${i}`} className="p-2 rounded bg-secondary/30 border border-border/30 space-y-1" data-testid={`insight-${i}`}>
                        {ins.positive && <p className="text-[11px] text-emerald-600 flex items-start gap-1"><CheckCircle2 className="w-3 h-3 shrink-0 mt-0.5" />{ins.positive}</p>}
                        {ins.warning && <p className="text-[11px] text-amber-600 flex items-start gap-1"><AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />{ins.warning}</p>}
                        <p className="text-[9px] text-muted-foreground">{ins.source}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* ─── My Deals + Recent Calls (50/50) ─── */}
        <div className="grid gap-5 lg:grid-cols-2">
          <Card className="bg-card border-border/50" data-testid="my-deals-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-foreground">My deals</CardTitle>
                <button onClick={() => navigate('/pipeline')} className="text-xs text-primary hover:underline">View all deals</button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {dashDeals.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">No deals yet</p> : dashDeals.map(deal => {
                const wp = getWP(deal.score);
                const stages = Object.entries(deal.stage_breakdown || {});
                return (
                  <div key={deal.company} className="p-3.5 rounded-lg border border-border/50 hover:border-primary/20 cursor-pointer transition-colors" onClick={() => navigate(`/report/${deal.call_id}`)} data-testid={`my-deal-${deal.company}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Building className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          <span className="text-sm font-semibold text-foreground truncate">{deal.company}</span>
                          <Badge className={`text-[9px] px-1.5 py-0 border ${classBadge(deal.grade)}`}>{deal.grade}</Badge>
                          {deal.deal_value > 0 && <span className="text-xs font-semibold text-primary">{formatValue(deal.deal_value)}</span>}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap text-[11px]">
                          <span className="flex items-center gap-1 text-muted-foreground"><Phone className="w-3 h-3" />{deal.total_calls}</span>
                          {stages.map(([s, c]) => <span key={s} className="px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{s} ({c})</span>)}
                          {deal.risk_count > 0 && <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-destructive/10 text-destructive"><AlertTriangle className="w-2.5 h-2.5" />{deal.risk_count}</span>}
                          {deal.next_steps?.[0] && <span className="text-muted-foreground truncate max-w-[180px]">Next: {deal.next_steps[0].slice(0, 35)}{deal.next_steps[0].length > 35 ? '...' : ''}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {deal.has_analysis && <div className="text-right"><p className="text-[10px] text-muted-foreground uppercase tracking-wider">Win Prob.</p><p className={`font-heading text-xl font-bold ${wp.color}`}>{wp.pct}%</p><p className={`text-[10px] font-medium ${wp.color}`}>{wp.label}</p></div>}
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <Card className="bg-card border-border/50" data-testid="recent-calls-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-foreground">Recent calls</CardTitle>
                <button onClick={() => navigate('/upload')} className="text-xs text-primary hover:underline">Upload call</button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {recentCalls.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">No analyzed calls yet</p> : recentCalls.map(call => {
                const wp = getWP(call.final_deal_score || 0);
                return (
                  <div key={call.call_id} className="p-3.5 rounded-lg border border-border/50 hover:border-primary/20 cursor-pointer transition-colors" onClick={() => navigate(`/report/${call.call_id}`)} data-testid={`recent-call-${call.call_id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                          <span className="text-sm font-semibold text-foreground truncate">{call.title}</span>
                          <Badge className={`text-[9px] px-1.5 py-0 border ${classBadge(call.grade || call.classification)}`}>{call.grade || call.classification}</Badge>
                          <Badge className={`text-[9px] px-1.5 py-0 border ${riskBadge(call.risk_status)}`}>{call.risk_status} Risk</Badge>
                        </div>
                        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                          {call.company_name && <span className="flex items-center gap-1"><Building className="w-3 h-3" />{call.company_name}</span>}
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDate(call.call_date || call.created_at)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {call.final_deal_score > 0 && <div className="text-right"><p className="text-[10px] text-muted-foreground">Win Prob.</p><p className={`font-heading text-xl font-bold ${wp.color}`}>{wp.pct}%</p></div>}
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
